import { useEffect, useState } from 'react';

import { cn } from '@lemoment/lib/utils';
import * as Popover from '@radix-ui/react-popover';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

interface DateFieldProps {
  /** yyyy-mm-dd */
  value: string;
  onChange: (iso: string) => void;
  /** yyyy-mm-dd — days before this are disabled. */
  min?: string;
}

export const DateField = ({ value, onChange, min }: DateFieldProps) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : new Date();
  const [view, setView] = useState(() => startOfMonth(selected));

  // Re-sync the visible month whenever the value changes externally.
  useEffect(() => {
    if (value) {
      setView(startOfMonth(parseISO(value)));
    }
  }, [value]);

  const monthStart = startOfMonth(view);
  const days = eachDayOfInterval({ start: monthStart, end: endOfMonth(view) });
  const leading = monthStart.getDay();
  const minDate = min ? startOfDay(parseISO(min)) : null;
  const today = new Date();

  const pick = (day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-card px-3 text-left text-sm transition-colors hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="font-numeral">
            {value ? format(parseISO(value), 'yyyy. MM. dd') : '날짜 선택'}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="start"
          className="z-50 w-[17rem] rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg outline-none"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="font-display text-sm font-semibold">
              {format(view, 'yyyy')}
              <span className="mx-1 text-muted-foreground">·</span>
              {format(view, 'M')}월
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                aria-label="이전 달"
                onClick={() => setView((v) => addMonths(v, -1))}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="다음 달"
                onClick={() => setView((v) => addMonths(v, 1))}
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {WEEKDAYS.map((label, i) => (
              <span
                key={label}
                className={cn(
                  'pb-1 text-center text-[0.65rem] font-medium',
                  i === 0 ? 'text-destructive/70' : 'text-muted-foreground',
                )}
              >
                {label}
              </span>
            ))}

            {Array.from({ length: leading }, (_, i) => (
              <span key={`b-${i}`} aria-hidden />
            ))}

            {days.map((day) => {
              const isSelected = value && isSameDay(day, selected);
              const isToday = isSameDay(day, today);
              const disabled = minDate ? isBefore(day, minDate) : false;
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  disabled={disabled}
                  onClick={() => pick(day)}
                  className={cn(
                    'font-numeral flex aspect-square items-center justify-center rounded-md text-[0.8rem] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    disabled
                      ? 'cursor-not-allowed text-muted-foreground/40 line-through decoration-1'
                      : isSelected
                        ? 'bg-primary font-semibold text-primary-foreground'
                        : isToday
                          ? 'text-lemon-ink ring-1 ring-inset ring-primary/40 hover:bg-accent dark:text-lemon'
                          : 'text-foreground hover:bg-accent',
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
